<template><div><h2 id="overview" tabindex="-1" class="d-docsite--header-2">Overview <a class="header-anchor d-link d-docsite--link d-link" href="#overview" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone uses a unified set of <code v-pre>layout</code> and <code v-pre>spacing</code> design tokens for all dimensional values. These tokens define both the intrinsic dimensions of UI surfaces (e.g. <code v-pre>width</code>, <code v-pre>height</code>) and the spatial relationships between them (e.g. <code v-pre>padding</code>, <code v-pre>gap</code>, positioning).</p>
<h3 id="layout-and-spacing-tokens" tabindex="-1" class="d-docsite--header-3">Layout and Spacing Tokens <a class="header-anchor d-link d-docsite--link d-link" href="#layout-and-spacing-tokens" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Layout and spacing tokens are CSS custom properties (a.k.a. CSS variables) generated from two base units:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Spacing</strong>: base <code v-pre>8px</code></li>
<li class="d-docsite--list-element"><strong>Layout</strong>: base <code v-pre>64px</code></li>
</ul>
<h4 class="d-docsite--header-4">Samples:</h4>
<table>
<thead>
<tr>
<th>Token</th>
<th>Math</th>
<th>Value</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>--dt-spacing-100</code></td>
<td>1 × 8px</td>
<td>8px</td>
</tr>
<tr>
<td><code v-pre>--dt-spacing-400</code></td>
<td>4 × 8px</td>
<td>32px</td>
</tr>
<tr>
<td><code v-pre>--dt-layout-100</code></td>
<td>1 × 64px</td>
<td>64px</td>
</tr>
<tr>
<td><code v-pre>--dt-layout-400</code></td>
<td>4 × 64px</td>
<td>256px</td>
</tr>
</tbody>
</table>
<p class="d-docsite--paragraph">Because the formula is deterministic, tools and code-generation assistants (including LLMs) can resolve any stop without needing the full reference table in context.</p>
<p class="d-docsite--paragraph">For the complete set, view <a href="/tokens/#spacing" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Spacing Tokens</a> and <a href="/tokens/#layout" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Layout Tokens</a>. For the story behind why layout and spacing live in separate families, see <RouteLink to="/dialtone/whats-new/posts/2026-3-9.html" class="d-docsite--link d-link">New Layout and Spacing Token Systems</RouteLink>.</p>
<h3 id="the-primitive-triad" tabindex="-1" class="d-docsite--header-3">The Primitive Triad <a class="header-anchor d-link d-docsite--link d-link" href="#the-primitive-triad" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">You rarely need these tokens directly. The <strong>primitive triad</strong> exposes them through three components:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong><RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink></strong>: what a container <em>is</em> (e.g. surface, border, padding, sizing)</li>
<li class="d-docsite--list-element"><strong><RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink></strong>: how children are <em>arranged</em> (e.g. direction, gap, alignment)</li>
<li class="d-docsite--list-element"><strong><RouteLink to="/components/text.html" class="d-docsite--link d-link">DtText</RouteLink></strong>: what the content <em>looks</em> like (e.g. font, size, tone)</li>
</ul>
<p class="d-docsite--paragraph">Compose the triad; reach for raw <a href="/tokens/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">design tokens</a> or <a href="#related-css-utilities" class="d-docsite--link d-link">CSS utilities</a> as an escape hatch.</p>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The primitive triad handles layout and spacing declaratively. For example:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">DtBox's <code v-pre>padding</code>: space <em>inside</em> a container, uses <code v-pre>--dt-spacing-*</code> design tokens</li>
<li class="d-docsite--list-element">DtBox's <code v-pre>min-inline-size</code>: container <em>width</em>, uses <code v-pre>--dt-layout-*</code> design tokens</li>
<li class="d-docsite--list-element">DtStack's <code v-pre>gap</code>: space <em>between</em> boxes, uses <code v-pre>--dt-spacing-*</code> design tokens</li>
</ul>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;200&quot;&gt;
  &lt;dt-box min-inline-size=&quot;300&quot; padding=&quot;200&quot; surface=&quot;moderate&quot; border-width=&quot;100&quot; border-radius=&quot;300&quot;&gt;
    &lt;dt-text as=&quot;p&quot; kind=&quot;body&quot; size=&quot;200&quot; tone=&quot;tertiary&quot; align=&quot;center&quot;&gt;Box 1&lt;/dt-text&gt;
  &lt;/dt-box&gt;
  &lt;dt-box min-inline-size=&quot;300&quot; padding=&quot;200&quot; surface=&quot;moderate&quot; border-width=&quot;100&quot; border-radius=&quot;300&quot;&gt;
    &lt;dt-text as=&quot;p&quot; kind=&quot;body&quot; size=&quot;200&quot; tone=&quot;tertiary&quot; align=&quot;center&quot;&gt;Box 2&lt;/dt-text&gt;
  &lt;/dt-box&gt;
  &lt;dt-box min-inline-size=&quot;300&quot; padding=&quot;200&quot; surface=&quot;moderate&quot; border-width=&quot;100&quot; border-radius=&quot;300&quot;&gt;
    &lt;dt-text as=&quot;p&quot; kind=&quot;body&quot; size=&quot;200&quot; tone=&quot;tertiary&quot; align=&quot;center&quot;&gt;Box 3&lt;/dt-text&gt;
  &lt;/dt-box&gt;
&lt;/dt-stack&gt;'>
<dt-stack gap="400" align="center">
  <dt-stack direction="row" :gap="selectedGap">
    <dt-box :min-inline-size="selectedSize" :padding="selectedPadding" surface="moderate" border-width="100" border-radius="300"><dt-text as="p" kind="body" size="200" tone="tertiary" align="center">Box 1</dt-text></dt-box>
    <dt-box :min-inline-size="selectedSize" :padding="selectedPadding" surface="moderate" border-width="100" border-radius="300"><dt-text as="p" kind="body" size="200" tone="tertiary" align="center">Box 2</dt-text></dt-box>
    <dt-box :min-inline-size="selectedSize" :padding="selectedPadding" surface="moderate" border-width="100" border-radius="300"><dt-text as="p" kind="body" size="200" tone="tertiary" align="center">Box 3</dt-text></dt-box>
  </dt-stack>
  <dt-stack direction="row" gap="200">
    <dt-select-menu size="200" label="Box's Padding" :options="paddingOptions" v-model="selectedPadding" />
    <dt-select-menu size="200" label="Box's Width" :options="sizeOptions" v-model="selectedSize" />
    <dt-select-menu size="200" label="Stack's Gap" :options="gapOptions" v-model="selectedGap" />
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="layout" tabindex="-1" class="d-docsite--header-3">Layout <a class="header-anchor d-link d-docsite--link d-link" href="#layout" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Prefer percentages, <code v-pre>min-inline-size</code>, or <code v-pre>min-block-size</code> so containers adapt to their context.</li>
<li class="d-docsite--list-element">Use <RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink>'s sizing props (<code v-pre>inline-size</code>, <code v-pre>min-inline-size</code>, <code v-pre>max-inline-size</code>, plus their block-axis equivalents) over raw <code v-pre>width</code> and <code v-pre>height</code>.</li>
<li class="d-docsite--list-element">Use fixed dimensions on elements that require them: avatars, icons, toggles.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid setting explicit dimensions (e.g. <code v-pre>width</code> or <code v-pre>height</code>) when a layout can flex.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="padding" tabindex="-1" class="d-docsite--header-3">Padding <a class="header-anchor d-link d-docsite--link d-link" href="#padding" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use <RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink>'s <code v-pre>padding</code> prop (or its directional variants <code v-pre>padding-inline</code> and <code v-pre>padding-block</code>) for space <em>inside</em> a container.</li>
<li class="d-docsite--list-element">Use <RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink>'s <code v-pre>gap</code> for space <em>between</em> sibling elements.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid using <code v-pre>margin</code> for layout. It lives outside the element, collapses in surprising ways, and may not scale well since it assumes a sibling element's presence. You might likely can achieve the same result with DtStack's <code v-pre>gap</code> or careful use of <code v-pre>padding</code>.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="arrangement" tabindex="-1" class="d-docsite--header-3">Arrangement <a class="header-anchor d-link d-docsite--link d-link" href="#arrangement" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use <RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink>'s <code v-pre>gap</code> to space siblings apart, vertically or horizontally.</li>
<li class="d-docsite--list-element">Pick smaller <code v-pre>--dt-spacing-*</code> values for gaps inside a container (between headings, text, buttons) and larger values for gaps between regions of a page.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid hand-rolling flex containers (<code v-pre>d-d-flex</code> + <code v-pre>d-ai-*</code> + <code v-pre>d-jc-*</code> + <code v-pre>d-g-*</code>) for layouts DtStack already expresses as props.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="grouping" tabindex="-1" class="d-docsite--header-3">Grouping <a class="header-anchor d-link d-docsite--link d-link" href="#grouping" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Group related elements tightly; separate unrelated ones widely. Proximity communicates relationship.</li>
<li class="d-docsite--list-element">Keep spacing decisions consistent across pages. Predictability reduces cognitive load.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don't vary gap sizes within a single UI pattern. Readers interpret inconsistent spacing as noise.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="guiding-the-eye" tabindex="-1" class="d-docsite--header-3">Guiding the Eye <a class="header-anchor d-link d-docsite--link d-link" href="#guiding-the-eye" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Give prominent elements more space around them; give supporting content less.</li>
<li class="d-docsite--list-element">Use empty space on purpose. Breathing room improves readability and contrast.</li>
<li class="d-docsite--list-element">Keep reading order in mind when deciding what to emphasize.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don't pack elements edge-to-edge. Crowded layouts bury hierarchy and fight reading order.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="text-rhythm" tabindex="-1" class="d-docsite--header-3">Text Rhythm <a class="header-anchor d-link d-docsite--link d-link" href="#text-rhythm" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Typography is the third leg of the triad.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use <RouteLink to="/components/text.html" class="d-docsite--link d-link">DtText</RouteLink> for all text. Its <code v-pre>density</code> prop controls line-height, so it governs rhythm <em>within</em> text.</li>
<li class="d-docsite--list-element">Pair DtText's <code v-pre>density</code> (within-text rhythm) with <RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink>'s <code v-pre>gap</code> (between-element rhythm).</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid combining typography utility classes (<code v-pre>d-fs-*</code>, <code v-pre>d-lh-*</code>, <code v-pre>d-fw-*</code>) when <RouteLink to="/components/text.html" class="d-docsite--link d-link">DtText</RouteLink> already exposes the right props.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="related-css-utilities" tabindex="-1" class="d-docsite--header-2">Related CSS Utilities <a class="header-anchor d-link d-docsite--link d-link" href="#related-css-utilities" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The triad covers most size and space needs. These utility classes remain available for cases the components don't cover.</p>
<table>
<thead>
<tr>
<th>Purpose</th>
<th>Utility</th>
<th>Prefer</th>
</tr>
</thead>
<tbody>
<tr>
<td><a href="/utilities/spacing/padding.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Padding</a></td>
<td><code v-pre>d-p-*</code></td>
<td><RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink> <code v-pre>padding</code> / <code v-pre>padding-inline</code> / <code v-pre>padding-block</code></td>
</tr>
<tr>
<td><a href="/utilities/spacing/margin.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Margin</a></td>
<td><code v-pre>d-m-*</code></td>
<td><RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink> <code v-pre>gap</code> for between-element spacing</td>
</tr>
<tr>
<td><a href="/utilities/flex/gap.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Flex / grid gap</a></td>
<td><code v-pre>d-g-*</code></td>
<td><RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink> <code v-pre>gap</code></td>
</tr>
<tr>
<td><a href="/utilities/sizing/width.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Width / height</a></td>
<td><code v-pre>d-w*</code> / <code v-pre>d-h*</code></td>
<td><RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink> <code v-pre>inline-size</code> / <code v-pre>block-size</code></td>
</tr>
<tr>
<td><a href="/utilities/sizing/min-width.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Min / max width</a></td>
<td><code v-pre>d-wmn-*</code> / <code v-pre>d-wmx-*</code></td>
<td><RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink> <code v-pre>min-inline-size</code> / <code v-pre>max-inline-size</code></td>
</tr>
<tr>
<td><a href="/utilities/flex/direction-wrap-flow.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Enable flex container</a></td>
<td><code v-pre>d-d-flex</code></td>
<td><RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink></td>
</tr>
<tr>
<td><a href="/utilities/flex/align-items.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Cross-axis alignment</a></td>
<td><code v-pre>d-ai-*</code></td>
<td><RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink> <code v-pre>align</code></td>
</tr>
<tr>
<td><a href="/utilities/flex/justify.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Main-axis distribution</a></td>
<td><code v-pre>d-jc-*</code></td>
<td><RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink> <code v-pre>justify</code></td>
</tr>
</tbody>
</table>
<p class="d-docsite--paragraph">For the full set, view <a href="/tokens/#spacing" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Spacing Tokens</a>, <a href="/tokens/#layout" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Layout Tokens</a>, and <a href="/utilities/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">CSS Utilities</a>.</p>
<h2 id="key-takeaways" tabindex="-1" class="d-docsite--header-2">Key Takeaways <a class="header-anchor d-link d-docsite--link d-link" href="#key-takeaways" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Let the triad do the work: <RouteLink to="/components/box.html" class="d-docsite--link d-link">DtBox</RouteLink> for the container, <RouteLink to="/components/stack.html" class="d-docsite--link d-link">DtStack</RouteLink> for arrangement, <RouteLink to="/components/text.html" class="d-docsite--link d-link">DtText</RouteLink> for content.</li>
<li class="d-docsite--list-element">Reach for tokens, not magic numbers. <code v-pre>--dt-layout-*</code> and <code v-pre>--dt-spacing-*</code> keep sizing and spacing consistent without manual pixel math.</li>
<li class="d-docsite--list-element">Use <code v-pre>padding</code> inside containers and <code v-pre>gap</code> between them. Avoid <code v-pre>margin</code> for layout wherever possible.</li>
<li class="d-docsite--list-element">Avoid fixed dimensions when a layout can flex; <code v-pre>min-*</code> constraints respond better across screen sizes.</li>
<li class="d-docsite--list-element">CSS utilities remain available for cases the triad doesn't cover, but start with the triad.</li>
</ul>
</div></template>


<script setup>
import { ref } from 'vue';

const paddingOptions = [
  { value: '100', label: 'padding="100"' },
  { value: '200', label: 'padding="200"' },
  { value: '300', label: 'padding="300"' },
  { value: '500', label: 'padding="500"' },
];

const sizeOptions = [
  { value: '100', label: 'min-inline-size="100"' },
  { value: '200', label: 'min-inline-size="200"' },
  { value: '300', label: 'min-inline-size="300"' },
];

const gapOptions = [
  { value: '100', label: 'gap="100"' },
  { value: '200', label: 'gap="200"' },
  { value: '300', label: 'gap="300"' },
  { value: '500', label: 'gap="500"' },
];

const selectedPadding = ref('200');
const selectedSize = ref('200');
const selectedGap = ref('200');
</script>